// Patient statements — who owes what, and a printable statement for each.
//
// Registry page (features/registry.js, `feature: 'statements'`). App.js renders
// <FeatureOff feature="statements"/> when the feature is off.
//
// NOTHING ON THIS PAGE SENDS ANYTHING. There is no email, no Stripe, no card
// charging — a statement is rendered into a sandboxed iframe and printed from the
// browser. The page says so out loud, because a page called "Statements" with a
// button on it is otherwise assumed to post mail. "Mark printed" is a local note to
// the biller working the list, not a state the backend keeps.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./StatementsPage.css";
import { formatCurrency } from "../../utils/formatCurrency";
import { formatPatientName } from "../../utils/formatName";
import { DATE_INPUT_MIN, DATE_INPUT_MAX } from "../../utils/dateInputBounds";
import { PayStatusChip } from "../collections";

const API_URL = process.env.REACT_APP_API_URL || "";

// A balance that is not positive has no statement. The service refuses to render one
// (there is nothing to bill), so the row must say why rather than offer a button that
// can only fail — and a credit must never be printed as "$-35.00", which reads to a
// biller as the patient owing minus thirty-five dollars. Neither should reach this
// table at all: `balances()` filters both out. They are handled anyway, because the
// day one slips through is the day a patient sees it.
function balanceCell(balance) {
  if (balance > 0) {
    return { text: formatCurrency(balance), className: "", statable: true };
  }
  if (balance < 0) {
    return {
      text: `${formatCurrency(Math.abs(balance))} credit`,
      className: "is-credit",
      statable: false,
      why: "This patient has been overpaid. There is nothing to bill — sort the credit out first.",
    };
  }
  return {
    text: "settled",
    className: "is-settled",
    statable: false,
    why: "Nothing outstanding, so there is no statement to produce.",
  };
}

export default function StatementsPage({ selectedPractice }) {
  const practiceId = selectedPractice?.id;

  const [since, setSince] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // See WorklistPage: only the newest request may write, so a practice switch
  // mid-flight cannot paint one practice's patients under another's name.
  const ticket = useRef(0);

  // Which statements this biller has printed in this sitting. Deliberately local:
  // nothing has been sent, so there is no fact about the patient to record. It is a
  // place-marker for working down a list of a hundred.
  const [printed, setPrinted] = useState(() => new Set());

  const [statement, setStatement] = useState(null); // {patient_id, name, html}
  const [statementLoading, setStatementLoading] = useState(false);
  const [provenance, setProvenance] = useState(null);
  const frameRef = useRef(null);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (since) p.set("since", since);
    return p.toString();
  }, [since]);

  const fetchBalances = useCallback(async () => {
    if (!practiceId) return;
    const mine = (ticket.current += 1);
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(
        `${API_URL}/api/payments/practices/${practiceId}/statements/balances${params ? `?${params}` : ""}`,
        { credentials: "include" },
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(
          body.error || `Could not load balances (${resp.status})`,
        );
      }
      const body = await resp.json().catch(() => null);
      if (mine !== ticket.current) return;
      // A 200 is not a payload. A truncated body or a proxy's HTML error page
      // parses to null, or to an object without `balances`, and `|| []` would then
      // render the "no patient owes anything" empty state — an all-clear produced at
      // exactly the moment we could not check. Refuse instead.
      if (!body || !Array.isArray(body.balances)) {
        throw new Error(
          "The response could not be read, so this list may be incomplete. Try again.",
        );
      }
      setRows(body.balances);
      setSummary({ count: body.count, total: body.total });
    } catch (err) {
      if (mine !== ticket.current) return;
      setError(err.message);
      setRows([]);
      setSummary(null);
    } finally {
      if (mine === ticket.current) setLoading(false);
    }
  }, [practiceId, params]);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  const openStatement = async (row) => {
    setStatementLoading(true);
    setError(null);
    setProvenance(null);
    try {
      const resp = await fetch(
        `${API_URL}/api/payments/practices/${practiceId}/patients/${row.patient_id}/statement${
          params ? `?${params}` : ""
        }`,
        { credentials: "include" },
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(
          body.error || `Could not render the statement (${resp.status})`,
        );
      }
      const body = await resp.json();
      setStatement({
        patient_id: row.patient_id,
        name: row.patient_name,
        html: body.html,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setStatementLoading(false);
    }
  };

  const loadProvenance = async (patientId) => {
    try {
      const resp = await fetch(
        `${API_URL}/api/payments/practices/${practiceId}/patients/${patientId}/statement/provenance${
          params ? `?${params}` : ""
        }`,
        { credentials: "include" },
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(
          body.error || `Could not load the postings (${resp.status})`,
        );
      }
      const body = await resp.json();
      // Keyed to what it was fetched FOR. An unkeyed array rendered patient A's
      // allocation ids, claims, dates and amounts under patient B after a
      // collapse-and-expand — on the one panel whose entire purpose is answering
      // "why does it say I owe $25", so the misattributed rows are the ones a
      // biller reads out to a patient.
      setProvenance({ patientId, since, postings: body.postings || [] });
    } catch (err) {
      setError(err.message);
    }
  };

  // The postings panel is open for this patient, at the filter it was fetched for.
  const provenanceShown = (patientId) =>
    !!provenance &&
    provenance.patientId === patientId &&
    provenance.since === since;

  const printStatement = () => {
    const frame = frameRef.current;
    if (!frame || !frame.contentWindow) return;
    frame.contentWindow.focus();
    frame.contentWindow.print();
    if (statement) {
      setPrinted((s) => new Set(s).add(statement.patient_id));
    }
  };

  if (!practiceId) {
    return (
      <div className="statements-page">
        <p className="statements-empty">
          Select a practice to see patient statements.
        </p>
      </div>
    );
  }

  return (
    <div className="statements-page">
      <div className="statements-header">
        <div>
          <h1>Patient statements</h1>
          <p className="statements-sub">
            What each patient owes {selectedPractice.name} — the responsibility
            their plan transferred, less what they have paid and anything
            written off for them.
          </p>
        </div>
        <button
          className="btn btn-secondary btn-sm"
          onClick={fetchBalances}
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="statements-notice">
        <strong>Nothing here sends.</strong> Atlas does not email statements or
        charge cards from this page. Open a statement to read it and print it
        (or save it as a PDF). Emailing a patient their statement, and charging
        a card they consented to, are separate deliberate steps on the
        Collections page — one patient at a time, each after a confirm.
      </div>

      <div className="statements-controls">
        <label className="statements-field">
          <span>Services on or after</span>
          <input
            type="date"
            min={DATE_INPUT_MIN}
            max={DATE_INPUT_MAX}
            value={since}
            onChange={(e) => setSince(e.target.value)}
          />
        </label>
        {since && (
          <button className="statements-clear" onClick={() => setSince("")}>
            Clear
          </button>
        )}
        {summary && (
          <div className="statements-summary">
            <span>
              <strong>{summary.count}</strong> patient
              {summary.count === 1 ? "" : "s"}
            </span>
            <span>
              <strong>{formatCurrency(summary.total)}</strong> outstanding
            </span>
            <span className="statements-printed-count">
              {printed.size} printed this session
            </span>
          </div>
        )}
      </div>

      {error && <div className="statements-message error">{error}</div>}

      {!loading && !error && rows.length === 0 && (
        <p className="statements-empty">
          No patient owes anything
          {since ? " for services on or after that date" : ""}. A statement is
          only produced for a positive balance — there is nothing to print.
        </p>
      )}

      {rows.length > 0 && (
        <div className="statements-table-wrap">
          <table className="statements-table">
            <thead>
              <tr>
                <th>Patient</th>
                <th className="num">Visits</th>
                <th className="num">Responsibility</th>
                <th className="num">Paid</th>
                <th className="num">Balance</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const resp = row.lines.reduce(
                  (sum, l) => sum + l.patient_resp,
                  0,
                );
                const paid = row.lines.reduce((sum, l) => sum + l.paid, 0);
                const cell = balanceCell(row.balance);
                return (
                  <React.Fragment key={row.patient_id}>
                    <tr
                      className={
                        printed.has(row.patient_id) ? "is-printed" : ""
                      }
                    >
                      <td className="statements-patient">
                        {formatPatientName(row.patient_name)}
                        <PayStatusChip
                          practiceId={practiceId}
                          patientId={row.patient_id}
                        />
                        {printed.has(row.patient_id) && (
                          <span className="printed-chip">printed</span>
                        )}
                      </td>
                      <td className="num">{row.lines.length}</td>
                      <td className="num">{formatCurrency(resp)}</td>
                      <td className="num">{formatCurrency(paid)}</td>
                      <td
                        className={`num statements-balance ${cell.className}`}
                        title={cell.why || undefined}
                      >
                        {cell.text}
                      </td>
                      <td className="statements-actions">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            // Clear on every toggle as well as keying the render:
                            // belt and braces on a panel that attributes money to a
                            // named person.
                            setProvenance(null);
                            setExpandedId(
                              expandedId === row.patient_id
                                ? null
                                : row.patient_id,
                            );
                          }}
                        >
                          {expandedId === row.patient_id
                            ? "Hide visits"
                            : "Visits"}
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => openStatement(row)}
                          disabled={statementLoading || !cell.statable}
                          title={cell.why || undefined}
                        >
                          Open statement
                        </button>
                      </td>
                    </tr>
                    {expandedId === row.patient_id && (
                      <tr className="statements-detail-row">
                        <td colSpan={6}>
                          <table className="statements-lines">
                            <thead>
                              <tr>
                                <th>Date of service</th>
                                <th>Plan</th>
                                <th className="num">Responsibility</th>
                                <th className="num">Paid</th>
                                <th className="num">Written off</th>
                                <th className="num">Balance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.lines.map((line) => (
                                <tr key={line.claim_id}>
                                  <td>{line.dos || "-"}</td>
                                  <td>{line.payer_name || "-"}</td>
                                  <td className="num">
                                    {formatCurrency(line.patient_resp)}
                                  </td>
                                  <td className="num">
                                    {formatCurrency(line.paid)}
                                  </td>
                                  <td className="num">
                                    {formatCurrency(line.written_off)}
                                  </td>
                                  <td className="num">
                                    {formatCurrency(line.balance)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {/* A toggle (David, staging walk 2026-09-15): the same
                              button hides what it showed. */}
                          <button
                            className="statements-provenance-btn"
                            aria-expanded={provenanceShown(row.patient_id)}
                            onClick={() =>
                              provenanceShown(row.patient_id)
                                ? setProvenance(null)
                                : loadProvenance(row.patient_id)
                            }
                          >
                            {provenanceShown(row.patient_id)
                              ? "Hide postings"
                              : "Show postings"}
                          </button>
                          {provenanceShown(row.patient_id) && (
                            <div className="statements-provenance">
                              {provenance.postings.length === 0 ? (
                                <p>No postings.</p>
                              ) : (
                                <table className="statements-lines">
                                  <thead>
                                    <tr>
                                      <th>Posting</th>
                                      <th>Claim</th>
                                      <th>DOS</th>
                                      <th>Source</th>
                                      <th className="num">Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {provenance.postings.map((p) => (
                                      <tr key={p.allocation_id}>
                                        <td>
                                          {p.posting_type}
                                          <span
                                            className={`direction-chip ${p.direction}`}
                                          >
                                            {p.direction}
                                          </span>
                                        </td>
                                        <td>#{p.claim_id}</td>
                                        <td>{p.dos || "-"}</td>
                                        <td>{p.source}</td>
                                        <td className="num">
                                          {formatCurrency(p.amount)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {statement && (
        <div
          className="statement-modal-backdrop"
          onClick={() => setStatement(null)}
        >
          <div className="statement-modal" onClick={(e) => e.stopPropagation()}>
            <div className="statement-modal-header">
              <div>
                <h2>Statement preview</h2>
                <p>{formatPatientName(statement.name)} — not sent to anyone</p>
              </div>
              <div className="statement-modal-actions">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={printStatement}
                >
                  Print
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setStatement(null)}
                >
                  Close
                </button>
              </div>
            </div>
            {/* sandbox without allow-scripts: the page is ours and carries no script,
                and the statement's own content is escaped server-side. Rendering it in
                an iframe rather than with dangerouslySetInnerHTML keeps its stylesheet
                out of the app's. */}
            <iframe
              ref={frameRef}
              className="statement-frame"
              title={`Statement for ${statement.name || "patient"}`}
              sandbox="allow-same-origin allow-modals"
              srcDoc={statement.html}
            />
          </div>
        </div>
      )}
    </div>
  );
}
